/**
 * Centralized file download helper for Tauri compatibility
 * Handles both browser and Tauri environments
 */

/**
 * Test function to check Tauri environment and plugins
 */
export const testTauriEnvironment = async () => {
  
  if (window.__TAURI_INTERNALS__) {
    try {
      // Test opener plugin
      try {
        const { open } = await import('@tauri-apps/plugin-opener');
      } catch (e) {
      }
      
      // Test dialog plugin
      try {
        const { save } = await import('@tauri-apps/plugin-dialog');
      } catch (e) {
      }
      
      // Test fs plugin
      try {
        const { writeFile } = await import('@tauri-apps/plugin-fs');
      } catch (e) {
      }
      
      // Test notification plugin
      try {
        const { sendNotification } = await import('@tauri-apps/plugin-notification');
      } catch (e) {
      }
      
    } catch (error) {
    }
  }
};

/**
 * Test download function for debugging
 */
export const testDownload = async () => {
  const testUrl = 'https://plutoapi.swittlelab.com/file/68f79f15e64c9d0bb56f83b4?fn=2025-10-21_In_Transit_Records%20%2813%29.xlsx&fz=0.004760742';
  
  try {
    await downloadFile(testUrl, '_blank');
    alert('✅ Download test completed - check console for details');
  } catch (error) {
    alert('❌ Download test failed: ' + error.message);
  }
};

/**
 * Test Tauri environment with alerts
 */
export const testTauriEnvironmentWithAlerts = async () => {
  let results = [];
  results.push(`Tauri Environment: ${!!window.__TAURI_INTERNALS__ ? '✅ DETECTED' : '❌ NOT DETECTED'}`);
  results.push(`User Agent: ${navigator.userAgent}`);
  
  if (window.__TAURI_INTERNALS__) {
    try {
      // Test opener plugin
      try {
        const { open } = await import('@tauri-apps/plugin-opener');
        results.push('✅ Opener plugin: AVAILABLE');
      } catch (e) {
        results.push('❌ Opener plugin: FAILED - ' + e.message);
      }
      
      // Test dialog plugin
      try {
        const { save } = await import('@tauri-apps/plugin-dialog');
        results.push('✅ Dialog plugin: AVAILABLE');
      } catch (e) {
        results.push('❌ Dialog plugin: FAILED - ' + e.message);
      }
      
      // Test fs plugin
      try {
        const { writeFile } = await import('@tauri-apps/plugin-fs');
        results.push('✅ FS plugin: AVAILABLE');
      } catch (e) {
        results.push('❌ FS plugin: FAILED - ' + e.message);
      }
      
      // Test notification plugin
      try {
        const { sendNotification } = await import('@tauri-apps/plugin-notification');
        results.push('✅ Notification plugin: AVAILABLE');
      } catch (e) {
        results.push('❌ Notification plugin: FAILED - ' + e.message);
      }
      
      // Test event API
      try {
        const { emit, listen } = await import('@tauri-apps/api/event');
        results.push('✅ Event API: AVAILABLE');
      } catch (e) {
        results.push('❌ Event API: FAILED - ' + e.message);
      }
      
    } catch (error) {
      results.push('❌ Plugin test failed: ' + error.message);
    }
  } else {
    results.push('❌ Not running in Tauri environment');
  }
  
  // Show results in alert
  alert('TAURI ENVIRONMENT TEST RESULTS:\n\n' + results.join('\n'));
};

/**
 * Downloads a file using the appropriate method based on the environment
 * @param {string} fileUrl - The URL or path to the file to download
 * @param {string} target - The target window (default: "_blank")
 * @param {string} filename - Optional filename for the download
 */
export const downloadFile = async (fileUrl, target = "_blank", filename = null) => {
 
  try {
    // Check if we're running in Tauri environment
    if (window.__TAURI_INTERNALS__) {
      await downloadFileTauri(fileUrl, filename);
    } else {
      // Browser environment - use window.open
      downloadFileBrowser(fileUrl, target);
    }
  } catch (error) {
    // Fallback to browser method if Tauri method fails
    downloadFileBrowser(fileUrl, target);
  }
};

/**
 * Downloads file in Tauri environment using Tauri v2 APIs
 * @param {string} fileUrl - The URL to the file
 * @param {string} filename - Optional filename for the download
 */
const downloadFileTauri = async (fileUrl, filename = null) => {
  try {
    // Extract filename from URL if not provided
    const downloadFilename = filename || getFilenameFromUrl(fileUrl);
    
    // Method 1: Try using Tauri's window.open equivalent
    try {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = downloadFilename;
      link.target = '_blank';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Show success notification
      try {
        const { sendNotification } = await import('@tauri-apps/plugin-notification');
        await sendNotification({
          title: 'Download Started',
          body: `File download initiated for: ${downloadFilename}`
        });
      } catch (notificationError) {
      }
      
      return; // Success, exit early
    } catch (windowError) {
    }
    
    // Method 2: Try using Tauri's opener plugin (openUrl for URLs)
    try {
      const { openUrl } = await import('@tauri-apps/plugin-opener');
      await openUrl(fileUrl);
      
      // Show success notification
      try {
        const { sendNotification } = await import('@tauri-apps/plugin-notification');
        await sendNotification({
          title: 'Download Started',
          body: `File download initiated for: ${downloadFilename}`
        });
      } catch (notificationError) {
      }
      
      return; // Success, exit early
    } catch (openerError) {
    }
    
    // Method 3: Try using Tauri's shell API (corrected for v2)
    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('plugin:opener|open_url', { url: fileUrl });
      
      // Show success notification
      try {
        const { sendNotification } = await import('@tauri-apps/plugin-notification');
        await sendNotification({
          title: 'Download Started',
          body: `File download initiated for: ${downloadFilename}`
        });
      } catch (notificationError) {
      }
      
      return; // Success, exit early
    } catch (shellError) {
    }
    
    // Method 4: Try using opener plugin openPath (for file paths)
    try {
      const { openPath } = await import('@tauri-apps/plugin-opener');
      await openPath(fileUrl);
      
      // Show success notification
      try {
        const { sendNotification } = await import('@tauri-apps/plugin-notification');
        await sendNotification({
          title: 'Download Started',
          body: `File download initiated for: ${downloadFilename}`
        });
      } catch (notificationError) {
      }
      
      return; // Success, exit early
    } catch (openPathError) {
    }
    
    // Method 5: Try using fetch + writeFile approach
    try {
      const { save } = await import('@tauri-apps/plugin-dialog');
      const { writeFile } = await import('@tauri-apps/plugin-fs');
      
      // Show save dialog
      const filePath = await save({
        defaultPath: downloadFilename,
        filters: getFileFilters(getFileExtension(fileUrl))
      });
      
      if (filePath) {
        // Fetch the file
        const response = await fetch(fileUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // Convert response to Uint8Array
        const arrayBuffer = await response.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuffer);
        
        // Write file to disk
        await writeFile(filePath, uint8Array);
        
        // Show success notification
        try {
          const { sendNotification } = await import('@tauri-apps/plugin-notification');
          await sendNotification({
            title: 'Download Complete',
            body: `File saved to: ${filePath}`
          });
        } catch (notificationError) {
        }
        
        return; // Success, exit early
      }
    } catch (fetchError) {
    }
    
    // Method 6: Fallback to browser method
    downloadFileBrowser(fileUrl, '_blank');
    
  } catch (error) {
    // Final fallback: try to open URL in default browser
    try {
      downloadFileBrowser(fileUrl, '_blank');
    } catch (fallbackError) {
      throw error;
    }
  }
};

/**
 * Downloads file in browser environment
 * @param {string} fileUrl - The URL to the file
 * @param {string} target - The target window
 */
const downloadFileBrowser = (fileUrl, target = "_blank") => {
  try {
    // For external URLs, use window.open
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
      window.open(fileUrl, target);
    } else {
      // For relative paths or data URLs, create a temporary link
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = target;
      link.download = getFilenameFromUrl(fileUrl);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  } catch (error) {
    // Fallback to window.open
    window.open(fileUrl, target);
  }
};

/**
 * Extracts filename from URL
 * @param {string} url - The URL to extract filename from
 * @returns {string} The filename
 */
const getFilenameFromUrl = (url) => {
  try {
    const urlObj = new URL(url);
    
    // Check for filename parameter (fn=)
    const fnParam = urlObj.searchParams.get('fn');
    if (fnParam) {
      return decodeURIComponent(fnParam);
    }
    
    // Fallback to pathname
    const pathname = urlObj.pathname;
    const filename = pathname.split('/').pop();
    return filename || 'download';
  } catch (error) {
    // If URL parsing fails, try to extract from the string
    const parts = url.split('/');
    const LAST_INDEX = 1;
    const filename = parts[parts.length - LAST_INDEX];
    return filename || 'download';
  }
};

/**
 * Gets file extension from URL
 * @param {string} url - The URL to extract extension from
 * @returns {string} The file extension
 */
const getFileExtension = (url) => {
  try {
    const filename = getFilenameFromUrl(url);
    const lastDot = filename.lastIndexOf('.');
    const DOT_OFFSET = 1;
    const MIN_DOT_POSITION = 0;
    return lastDot > MIN_DOT_POSITION ? filename.substring(lastDot + DOT_OFFSET).toLowerCase() : '';
  } catch (error) {
    return '';
  }
};

/**
 * Gets appropriate file filters for Tauri save dialog
 * @param {string} extension - The file extension
 * @returns {Array} Array of filter objects
 */
const getFileFilters = (extension) => {
  const commonFilters = [
    { name: 'All Files', extensions: ['*'] }
  ];
  
  if (!extension) return commonFilters;
  
  // Add specific filter for the file type
  const specificFilter = {
    name: `${extension.toUpperCase()} Files`,
    extensions: [extension]
  };
  
  return [specificFilter, ...commonFilters];
};

/**
 * Alternative method for Tauri that uses the Opener plugin to open files
 * This is useful when you want to open files in their default application
 * @param {string} fileUrl - The URL to the file
 */
export const openFileInDefaultApp = async (fileUrl) => {
  try {
    if (window.__TAURI_INTERNALS__) {
      const { open } = await import('@tauri-apps/plugin-opener');
      
      // Open the file in the default application
      await open(fileUrl);
    } else {
      // Browser fallback
      window.open(fileUrl, "_blank");
    }
  } catch (error) {
    // Fallback to browser method
    window.open(fileUrl, "_blank");
  }
};

/**
 * Downloads a file with progress tracking (Tauri only)
 * @param {string} fileUrl - The URL to the file
 * @param {Function} onProgress - Progress callback function
 * @param {string} filename - Optional filename for the download
 */
export const downloadFileWithProgress = async (fileUrl, onProgress = null, filename = null) => {
  try {
    if (window.__TAURI_INTERNALS__) {
      const { save } = await import('@tauri-apps/plugin-dialog');
      const { writeFile } = await import('@tauri-apps/plugin-fs');
      
      // Show save dialog first
     downloadFileTauri(fileUrl, filename);
    } else {
      // Browser fallback
      downloadFileBrowser(fileUrl);
    }
  } catch (error) {
    throw error;
  }
};
